import { useState, memo, useMemo } from 'react';
// mantine
import { Radio, Stack, TextInput } from '@mantine/core';
// icons
import { Search } from 'lucide-react';

// components
import AsyncList from '@/modules/Integrations/components/Selection/components/AsyncList';
import List from '@/modules/Integrations/components/Selection/components/List';
// utils
import { usePossibleValuesPaginatedQuery } from '@/services/possible_values/possible_values.queries.ts';

interface SelectionProps {
    label: string;
    description?: string;
    error?: string;
    value: string;
    onChange: (value: string) => void;
    data: string[] | null;
    url?: string;
    withAsterisk?: boolean;
}

const Selection = ({
    data,
    description,
    error,
    label,
    onChange,
    value,
    url = '',
    withAsterisk
}: SelectionProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };
    const {
        data: response,
        hasNextPage,
        fetchNextPage,
        isFetching
    } = usePossibleValuesPaginatedQuery({
        url,
        params: {
            query: searchTerm,
            per: 20
        }
    });

    const items = useMemo(() => {
        return (response?.pages ?? []).flatMap((page) => page.items);
    }, [response?.pages]);

    const handleRangeChange = (_: number, end: number) => {
        const SHIFT = 20;
        if (end + SHIFT > items.length && hasNextPage && !isFetching) {
            fetchNextPage();
        }
    };

    return (
        <div>
            <Radio.Group
                description={description}
                error={error}
                label={label}
                value={value}
                withAsterisk={withAsterisk}
                onChange={onChange}
            >
                <Stack className="gap-1 py-2">
                    <TextInput
                        leftSection={<Search />}
                        placeholder="Search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />

                    {url ? (
                        <AsyncList
                            items={items}
                            onRangeChange={handleRangeChange}
                        />
                    ) : (
                        <List items={data ?? []} searchTerm={searchTerm} />
                    )}
                </Stack>
            </Radio.Group>
        </div>
    );
};

export default memo(Selection);
